import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import Login from "./Login";
import Onboarding from "./Onboarding";
import EnrollPlayer from "./EnrollPlayer";
import PlayerList from "./PlayerList";
import FeeTracker from "./FeeTracker";
import Dashboard from "./Dashboard";
import Attendance from "./Attendance";

// Icons as clean SVG components
const HomeIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"
      stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8"
      strokeLinejoin="round" fill={active ? "rgba(212,160,23,0.1)" : "none"}/>
  </svg>
);

const PlayersIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="4" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8"/>
    <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M16 3.13a4 4 0 010 7.75" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M21 21v-2a4 4 0 00-3-3.85" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const FeesIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8"/>
    <path d="M12 7v1m0 8v1m-3-5h4.5a1.5 1.5 0 010 3H9a1.5 1.5 0 010-3h.5m0 0V9.5A1.5 1.5 0 0111 8h2" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const AttendanceIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8"/>
    <path d="M16 2v4M8 2v4M3 10h18" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M8 15l2.5 2.5L16 12" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SettingsIcon = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="3" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8"/>
    <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M18.66 5.34l-1.41 1.41" stroke={active ? "#d4a017" : "#6b7280"} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

function Settings({ onSignOut, academy, onAcademyUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: academy?.name || "", city: academy?.city || "" });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const saveAcademy = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const { data, error } = await supabase
      .from("academies")
      .update({ name: form.name.trim(), city: form.city.trim() })
      .eq("id", academy.id)
      .select()
      .single();
    if (!error) {
      onAcademyUpdate(data);
      setEditing(false);
      setToast("✅ Academy updated!");
      setTimeout(() => setToast(""), 3000);
    }
    setSaving(false);
  };

  const inputStyle = { width: "100%", padding: "0.75rem 1rem", border: "1.5px solid #d4a017", fontSize: "0.95rem", fontFamily: "-apple-system, sans-serif", outline: "none", background: "white", boxSizing: "border-box", marginBottom: "0.8rem" };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#f0ebe0", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <div style={{ background: "#111a12", padding: "1.4rem 1.5rem 1.8rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.2rem" }}>
          <div style={{ width: "32px", height: "32px", background: "rgba(212,160,23,0.15)", border: "1px solid rgba(212,160,23,0.4)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={{ color: "white", fontSize: "0.72rem", fontWeight: "800", letterSpacing: "3px" }}>
            {academy?.name?.toUpperCase() || "THE COACH ROOM"}
          </span>
        </div>
        <h2 style={{ color: "white", fontSize: "1.5rem", fontWeight: "700", margin: 0 }}>Settings</h2>
        {academy?.city && <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", margin: "0.3rem 0 0" }}>{academy.city}</p>}
      </div>

      <div style={{ padding: "1.5rem" }}>
        <div style={{ background: "white", border: "1px solid #e8e0d0", padding: "1.2rem", marginBottom: "0.8rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1px", color: "#6b7280" }}>Academy Details</div>
            {!editing && (
              <button onClick={() => setEditing(true)} style={{ background: "none", border: "1px solid #d4a017", color: "#d4a017", padding: "0.25rem 0.7rem", fontSize: "0.72rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, sans-serif" }}>
                Edit
              </button>
            )}
          </div>
          {editing ? (
            <>
              <input style={inputStyle} placeholder="Academy Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input style={inputStyle} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <div style={{ display: "flex", gap: "0.6rem" }}>
                <button onClick={saveAcademy} disabled={saving} style={{ flex: 1, padding: "0.8rem", background: "#1a3a1b", color: "#d4a017", border: "none", fontSize: "0.85rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, sans-serif" }}>
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={() => { setEditing(false); setForm({ name: academy?.name || "", city: academy?.city || "" }); }} style={{ padding: "0.8rem 1rem", background: "#f0ebe0", color: "#6b7280", border: "1px solid #e8e0d0", fontSize: "0.85rem", fontWeight: "600", cursor: "pointer", fontFamily: "-apple-system, sans-serif" }}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: "1rem", fontWeight: "700", color: "#1a1a1a" }}>{academy?.name}</div>
              {academy?.city && <div style={{ fontSize: "0.82rem", color: "#6b7280", marginTop: "0.2rem" }}>{academy.city}</div>}
            </>
          )}
        </div>

        <div style={{ background: "white", border: "1px solid #e8e0d0", padding: "1.2rem", marginBottom: "1.5rem" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: "800", textTransform: "uppercase", letterSpacing: "1px", color: "#6b7280", marginBottom: "0.5rem" }}>More features coming</div>
          <div style={{ fontSize: "0.82rem", color: "#9ca3af", fontWeight: "300", lineHeight: "1.6" }}>Batch management, notification preferences and more coming soon.</div>
        </div>

        <button style={{ width: "100%", padding: "1rem", background: "#fde8de", color: "#9b1c1c", border: "1px solid #feb2b2", fontSize: "0.88rem", fontWeight: "700", cursor: "pointer", fontFamily: "-apple-system, sans-serif" }} onClick={onSignOut}>
          Sign Out
        </button>
      </div>
      {toast && <div style={{ position: "fixed", bottom: "5rem", left: "50%", transform: "translateX(-50%)", background: "#1a3a1b", color: "white", padding: "0.7rem 1.4rem", borderRadius: "20px", fontSize: "0.82rem", fontWeight: "600", whiteSpace: "nowrap", zIndex: 100 }}>{toast}</div>}
    </div>
  );
}


// Main App with bottom nav
export default function App() {
  const [activeTab, setActiveTab] = useState("home");
  const [showEnroll, setShowEnroll] = useState(false);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [academy, setAcademy] = useState(null);
  const [academyLoading, setAcademyLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchAcademy(session.user.id);
      else setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchAcademy(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchAcademy = async (userId) => {
    setAcademyLoading(true);
    const { data } = await supabase
      .from("academies")
      .select("*")
      .eq("user_id", userId)
      .single();
    setAcademy(data || null);
    setAuthLoading(false);
    setAcademyLoading(false);
  };

  if (authLoading || academyLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0f1f14", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#d4a017", fontFamily: "-apple-system, sans-serif", fontSize: "0.9rem", fontWeight: "600", letterSpacing: "2px" }}>THE COACH ROOM</div>
      </div>
    );
  }

  if (!session) {
    return <Login onLogin={(session) => setSession(session)} />;
  }

  if (!academy) {
    return <Onboarding user={session.user} onComplete={(a) => setAcademy(a)} />;
  }

  const tabs = [
    { id: "home", label: "Home", Icon: HomeIcon },
    { id: "players", label: "Players", Icon: PlayersIcon },
    { id: "fees", label: "Fees", Icon: FeesIcon },
    { id: "attendance", label: "Attendance", Icon: AttendanceIcon },
    { id: "settings", label: "Settings", Icon: SettingsIcon },
  ];

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  const renderScreen = () => {
    if (showEnroll) {
      return <EnrollPlayer onBack={() => setShowEnroll(false)} academy={academy} />;
    }
    switch (activeTab) {
      case "home": return <Dashboard setActiveTab={setActiveTab} academy={academy} />;
      case "players": return <PlayerList onEnroll={() => setShowEnroll(true)} academy={academy} />;
      case "fees": return <FeeTracker academy={academy} />;
      case "attendance": return <Attendance academy={academy} />;
      case "settings": return <Settings onSignOut={signOut} academy={academy} onAcademyUpdate={(a) => setAcademy(a)} />;
      default: return <Dashboard setActiveTab={setActiveTab} academy={academy} />;
    }
  };

  return (
    <div style={styles.shell}>
      {/* SCREEN */}
      <div style={styles.screen}>
        {renderScreen()}
      </div>

      {/* BOTTOM NAV — always visible */}
      <div style={styles.bottomNav}>
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            style={{
              ...styles.tabBtn,
              background: activeTab === id ? "rgba(212,160,23,0.06)" : "transparent",
            }}
            onClick={() => {
              setShowEnroll(false);
              setActiveTab(id);
            }}
          >
            <div style={styles.tabIcon}>
              <Icon active={activeTab === id && !showEnroll} />
              {activeTab === id && !showEnroll && <div style={styles.activeDot} />}
            </div>
            <span style={{
              ...styles.tabLabel,
              color: activeTab === id && !showEnroll ? "#d4a017" : "#9ca3af",
              fontWeight: activeTab === id && !showEnroll ? "700" : "400",
            }}>
              {label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const styles = {
  shell: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#0f1f14",
    maxWidth: "480px",
    margin: "0 auto",
    position: "relative",
    boxShadow: "0 0 60px rgba(0,0,0,0.5)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  screen: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  bottomNav: {
    display: "flex",
    background: "#111a12",
    borderTop: "1px solid rgba(255,255,255,0.06)",
    padding: "0.4rem 0 0.6rem",
    flexShrink: 0,
  },
  tabBtn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.25rem",
    border: "none",
    cursor: "pointer",
    padding: "0.4rem 0",
    transition: "background 0.15s",
  },
  tabIcon: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  activeDot: {
    position: "absolute",
    bottom: "-4px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    background: "#d4a017",
  },
  tabLabel: {
    fontSize: "0.6rem",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
};